import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { Popover, Stack, Typography } from '@mui/material'
import {
  buildDiscrepancyBreakdown,
  CellDiscrepancy,
} from '@/utils/po-discrepancies'

type DiscrepancyPopoverProps = {
  open: boolean
  anchorEl: HTMLElement | null
  detail: CellDiscrepancy | null
  onClose: () => void
}

/**
 * Click-triggered breakdown of a single line-item discrepancy. Opens to the
 * right of the clicked cell (vertically centered on it) so it never covers the
 * rows above or below. Replaces the old hover tooltip: the structured values
 * the endpoint returns (invoice value, PO remaining, what other invoices have
 * consumed, what's available, how far over) are laid out as an itemized
 * breakdown so the "why" is explicit rather than crammed into one sentence.
 */
export default function DiscrepancyPopover({
  open,
  anchorEl,
  detail,
  onClose,
}: DiscrepancyPopoverProps) {
  const breakdown = detail ? buildDiscrepancyBreakdown(detail) : null

  return (
    <Popover
      open={open && !!anchorEl && !!breakdown}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      slotProps={{
        paper: { sx: { borderRadius: 2, minWidth: 240, maxWidth: 320, ml: 1 } },
      }}
    >
      {breakdown && (
        <Stack sx={{ p: 1.5 }} spacing={1.25}>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningAmberIcon sx={{ fontSize: 18, color: 'warning.main' }} />
            <Typography variant="subtitle2" fontWeight={700}>
              {breakdown.title}
            </Typography>
          </Stack>
          <Stack spacing={0.5}>
            {breakdown.rows.map((row) => (
              <Stack
                key={row.label}
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
                spacing={2}
                sx={
                  row.emphasis === 'available'
                    ? {
                        pt: 0.5,
                        mt: 0.25,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      }
                    : undefined
                }
              >
                <Typography
                  variant="body2"
                  color={
                    row.emphasis === 'over' ? 'error.main' : 'text.secondary'
                  }
                  fontWeight={row.emphasis ? 700 : 400}
                >
                  {row.label}
                </Typography>
                <Typography
                  variant="body2"
                  color={
                    row.emphasis === 'over' ? 'error.main' : 'text.primary'
                  }
                  fontWeight={row.emphasis ? 700 : 500}
                  sx={{
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.value}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}
    </Popover>
  )
}
